import type { ComponentProps, ReactNode } from 'react';

import { faTimes } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Rnd } from 'react-rnd';

import { cn } from '../lib/utils';

import { ModeToggle } from './mode-toggle';

export interface ResizableDraggableProps extends ComponentProps<typeof Rnd> {
  children: ReactNode;
  disableCloseOnOverlayClick?: boolean;
  enableThemeToggle?: boolean;
  headerActions?: ReactNode;
  headerContent: ReactNode;
  mode?: 'dark' | 'light';
  onClose: () => void;
  onThemeToggle?: () => void;
}

export const ResizableDraggable = ({
  bounds = 'window',
  children,
  className,
  disableCloseOnOverlayClick = false,
  enableThemeToggle = false,
  headerActions,
  headerContent,
  minHeight = 400,
  minWidth = 500,
  mode = 'dark',
  onClose,
  onThemeToggle,
  ...rndProps
}: ResizableDraggableProps) => {
  const isDarkMode = mode === 'dark';

  return (
    <>
      {!disableCloseOnOverlayClick && (
        <div
          role="presentation"
          aria-hidden
          onClick={onClose}
          className="vai-fixed vai-inset-0 vai-z-[9997] vai-cursor-default"
        />
      )}
      <Rnd
        dragHandleClassName="vai-resizable-draggable-header"
        minWidth={minWidth}
        minHeight={minHeight}
        bounds={bounds}
        className={cn(
          'vai-rounded-lg vai-border vai-shadow-lg vai-overflow-hidden vai-flex vai-flex-col vai-z-[9998]',
          {
            'vai-bg-zinc-950 vai-border-zinc-700': isDarkMode,
            'vai-bg-white vai-border-zinc-200': !isDarkMode,
          },
          className,
        )}
        {...rndProps}
      >
        <div className="vai-h-full vai-min-h-0 vai-flex vai-flex-col vai-overflow-hidden">
          <div className="vai-absolute vai-top-1 vai-right-2 vai-flex vai-items-center vai-justify-between vai-gap-2 vai-z-10">
            {headerActions}
            {enableThemeToggle && (
              <ModeToggle mode={mode} onClick={onThemeToggle} />
            )}
            <button
              type="button"
              onClick={onClose}
              className={cn('vai-rounded-sm vai-p-1 vai-text-zinc-500', {
                'hover:vai-text-zinc-100': isDarkMode,
                'hover:vai-text-zinc-950': !isDarkMode,
              })}
              aria-label="Close"
            >
              <FontAwesomeIcon size="lg" icon={faTimes} />
            </button>
          </div>

          <div
            className={cn(
              'vai-resizable-draggable-header vai-cursor-move vai-px-2 vai-py-[10px] vai-flex vai-gap-4 vai-border-b',
              {
                'vai-bg-zinc-800 vai-border-zinc-700': isDarkMode,
                'vai-bg-zinc-50 vai-border-zinc-200': !isDarkMode,
              },
            )}
          >
            <FontAwesomeIcon
              className="vai-text-zinc-500 vai-mt-[2px]"
              icon={faGripVertical}
            />
            {headerContent}
          </div>

          <div
            className="vai-flex-1 vai-overflow-auto vai-min-h-0 vai-p-3"
            style={{
              scrollbarColor: '#52525b transparent',
            }}
          >
            {children}
          </div>
        </div>
      </Rnd>
    </>
  );
};
